import { Box } from '@mui/material';
import {
  Node as ReactFlowNode,
  NodeProps,
  NodeResizer,
  Position,
  useInternalNode,
} from '@xyflow/react';
import { ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  EdgeLabel,
  EdgeLabelProps,
} from '@/components/diagrams/components/EdgeLabel';
import { COLORS } from '@/styles/tokens/colors';

import { Handle, HandleProps } from '../../../components/Handle';
import { useDraggingNode } from '../../hooks/useDraggingNode';
import { useIsInteractive } from '../../hooks/useIsInteractive';
import { useNodeControlsHover } from '../../hooks/useNodeControlsHover';
import { useResizeAllSelectedNodes } from '../../hooks/useResizeAllSelectedNodes';
import { useSelectedNodes } from '../../hooks/useSelectedNodes';
import {
  NODE_RESIZER_HANDLE_STYLE,
  NODE_RESIZER_LINE_STYLE,
} from '../nodeResizerStyles';
import { TileNodeControls } from '../TileNode/TileNodeControls/TileNodeControls';
import {
  RELATIONSHIP_NODE_HEIGHT,
  RELATIONSHIP_NODE_WIDTH,
} from './relationshipLabelSize';

// The smallest size of a resized label. The height keeps one line of label
// text visible.
const MIN_NODE_WIDTH = 60;
const MIN_NODE_HEIGHT = RELATIONSHIP_NODE_HEIGHT;

// A wire is drawn 1px wide, so a joint of 3px reads as a thickening of the wire
// where it meets the label rather than as a mark of its own, or as a handle to
// take hold of.
const WIRE_JOINT_SIZE_PX = 3;

export interface RelationshipLabelNodeData extends Record<string, unknown> {
  label: string;
  value?: string;
  variant?: EdgeLabelProps['variant'];
  hideNodeResizer?: boolean;
  hideNodeControls?: boolean;
  controls?: ReactNode;
  /**
   * Mark where the wire meets the label, at the top and bottom of it.
   *
   * A label that rides its wire is a box the wire passes behind, and a reader
   * cannot always tell which wire that is. The two marks name the wire by
   * showing where it goes in and where it comes out.
   */
  showWireJoints?: boolean;
  /**
   * Which way the wire runs where the label sits. The joints go on the two
   * sides the wire meets: top and bottom where it drops, left and right where it
   * runs across.
   */
  wireJointsAxis?: 'horizontal' | 'vertical';
  /**
   * The user manually positioned this label, so automatic layout preserves its
   * coordinates and routes the transfer through it.
   */
  isPositionPinned?: boolean;
}

export type RelationshipLabelNodeProps = NodeProps<
  ReactFlowNode<RelationshipLabelNodeData>
>;

const ALL_POSITIONS = Object.values(Position);

// Stretch the EdgeLabel to fill the outer Box's width so long labels wrap at
// the node's edge. We do NOT force `height: 100%` — leaving height content-
// driven lets the node grow vertically for multi-line labels. When selected,
// darken the background and recolor the EdgeLabel's existing 1px border
// (matching how tile nodes signal selection — they recolor their 1px outline
// rather than stacking an extra ring, so the selected edge stays one pixel).
const useFillRootStyles = makeStyles<{ selected: boolean }>()(
  (_theme, { selected }) => ({
    fillRoot: {
      display: 'flex',
      width: '100%',
      ...(selected && {
        backgroundColor: COLORS.GRAY[200],
        borderColor: COLORS.NAVY[900],
        // The EdgeLabel variant recolors its border on `:hover`; keep the
        // selected color through hover (as tile nodes do) so the selected
        // outline doesn't flicker back to gray.
        '&:hover': { borderColor: COLORS.NAVY[900] },
      }),
    },
  })
);

export function RelationshipLabelNode({
  id,
  data: {
    label,
    value,
    variant = 'primary',
    hideNodeResizer = false,
    hideNodeControls = false,
    controls,
    showWireJoints = false,
    wireJointsAxis = 'vertical',
  },
  isConnectable,
  selected,
  dragging,
}: RelationshipLabelNodeProps) {
  // The width/height props from NodeProps merge in React Flow's *measured*
  // dimensions (and are 0 before the first measurement), so they can't
  // distinguish a user-resized node from an auto-sized one. Read the explicit
  // dimensions off the node instead — they are set only by a resize gesture or
  // by persisted (previously resized) dimensions. Un-resized nodes keep them
  // undefined and continue to auto-size from content.
  const internalNode = useInternalNode(id);
  const width = internalNode?.width;
  const height = internalNode?.height;
  const { classes } = useFillRootStyles({ selected: !!selected });
  const { isInteractive } = useIsInteractive();
  const { draggingNode } = useDraggingNode();
  const onResize = useResizeAllSelectedNodes(id);
  const { selectedNodes } = useSelectedNodes();
  const { isHovered, hoverProps } = useNodeControlsHover();
  const resizeHandlesVisible = !!selected && isInteractive && !dragging;
  // Selection keeps its existing visibility behavior; the global drag check
  // prevents a hovered toolbar from appearing while another node is dragged.
  const nodeControlsVisible =
    (resizeHandlesVisible && selectedNodes.length <= 1) ||
    (isInteractive &&
      !dragging &&
      !draggingNode &&
      selectedNodes.length <= 1 &&
      isHovered);
  const commonHandleProps: Partial<HandleProps> = {
    isConnectable,
    hidden: true,
  };

  // Fix the width to RELATIONSHIP_NODE_WIDTH so long labels wrap at a
  // consistent place, but let height auto-size from content. The resulting
  // bounding box matches the visible EdgeLabel exactly, so helper lines
  // align with the EdgeLabel edges and arrow edges terminate flush with
  // its border in both single- and multi-line cases. Once a user resizes the
  // node, React Flow sets explicit width/height and those take over.
  return (
    <>
      {!hideNodeResizer && (
        <NodeResizer
          nodeId={id}
          lineStyle={NODE_RESIZER_LINE_STYLE}
          handleStyle={NODE_RESIZER_HANDLE_STYLE}
          isVisible={resizeHandlesVisible}
          onResize={onResize}
          minWidth={MIN_NODE_WIDTH}
          minHeight={MIN_NODE_HEIGHT}
        />
      )}
      <Box
        sx={{
          width: width ?? RELATIONSHIP_NODE_WIDTH,
          height,
          display: 'flex',
        }}
        {...hoverProps}
      >
        <EdgeLabel
          label={label}
          value={value}
          variant={variant}
          classes={{ root: classes.fillRoot }}
        />
      </Box>
      {showWireJoints &&
        (['start', 'end'] as const).map((end) => {
          const runsAcross = wireJointsAxis === 'horizontal';
          const edge = runsAcross
            ? end === 'start'
              ? 'left'
              : 'right'
            : end === 'start'
              ? 'top'
              : 'bottom';
          const shift = end === 'start' ? '-50%' : '50%';
          return (
            <Box
              key={end}
              sx={{
                position: 'absolute',
                [edge]: 0,
                // The label's height is an odd number of pixels, so half of it
                // lands between two of them and a joint on a sideways run sits a
                // pixel above the wire. Its width is even, so a joint on a drop
                // needs no such nudge.
                ...(runsAcross ? { top: 'calc(50% + 1px)' } : { left: '50%' }),
                width: WIRE_JOINT_SIZE_PX,
                height: WIRE_JOINT_SIZE_PX,
                borderRadius: '50%',
                // The colour a wire is drawn in, so a joint belongs to the wire
                // and not to the label.
                backgroundColor: COLORS.GRAY[400],
                transform: runsAcross
                  ? `translate(${shift}, -50%)`
                  : `translate(-50%, ${shift})`,
                pointerEvents: 'none',
              }}
            />
          );
        })}
      {!hideNodeControls && controls && (
        <TileNodeControls isVisible={nodeControlsVisible} {...hoverProps}>
          {controls}
        </TileNodeControls>
      )}
      {ALL_POSITIONS.map((position) => (
        <Handle
          key={`target-${position}`}
          type="target"
          {...commonHandleProps}
          id={position}
          position={position}
        />
      ))}
      {ALL_POSITIONS.map((position) => (
        <Handle
          key={`source-${position}`}
          type="source"
          {...commonHandleProps}
          id={position}
          position={position}
        />
      ))}
    </>
  );
}
